import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Mic, BookOpen } from "lucide-react";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const HomePage = () => {
  const offerings = [
    {
      icon: <Users size={28} />,
      title: "Somale Circles",
      description: "Community healing spaces for Black survivors. Monthly gatherings centered on embodiment, consent, and collective restoration.",
      link: "/offerings"
    },
    {
      icon: <Heart size={28} />,
      title: "One-on-One Coaching",
      description: "Personalized somatic support for reclaiming your body as a site of power, pleasure, and self-determination.",
      link: "/offerings"
    },
    {
      icon: <Mic size={28} />,
      title: "Speaking & Training",
      description: "Educational engagements for institutions, health systems, and organizations on trauma-responsive embodiment.",
      link: "/offerings"
    },
    {
      icon: <BookOpen size={28} />,
      title: "Somatic Education",
      description: "Monthly subscription-based learning through community workshops exploring the body as a site of liberation.",
      link: "/offerings"
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen relative flex items-center" data-testid="hero-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-bg-primary/70 z-10" />
          <img
            src="https://images.unsplash.com/photo-1510798259630-402bafce75d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBncmVlbiUyMGdvbGQlMjBsaXF1aWQlMjBtYXJibGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc3MjAwMTc5MHww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="section-container relative z-20 pt-24 sm:pt-32 pb-12 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Text Content */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="order-2 lg:order-1"
            >
              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.6 }}
                className="font-body text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-brand-accent mb-4 sm:mb-6"
              >
                Embodied Healing Justice
              </motion.p>
              
              <motion.h1
                variants={fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-text-primary leading-tight mb-5 sm:mb-8"
              >
                Your Body is a{" "}
                <span className="italic text-brand-accent">Sacred</span>{" "}
                Site of Sovereignty
              </motion.h1>

              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="font-accent text-lg sm:text-xl md:text-2xl text-text-secondary leading-relaxed mb-5 sm:mb-8"
              >
                "The ability to access sensation, to feel safe in the body, is not a luxury — 
                it is a fundamental human right."
              </motion.p>

              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="font-body text-sm sm:text-base text-text-secondary leading-relaxed mb-6 sm:mb-10"
              >
                I'm Inas-Khalidah Mahdi — survivor, embodied researcher, and healing justice 
                practitioner. Through Somale Restoration Collective, I guide Black survivors 
                in reclaiming the body as a site of power, pleasure, and collective liberation.
              </motion.p>

              <motion.div
                variants={fadeUp}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-3 sm:gap-4"
              >
                <Link to="/book" data-testid="hero-cta-book" className="btn-primary text-center">
                  Begin Your Journey
                </Link>
                <Link to="/about" data-testid="hero-cta-about" className="btn-secondary text-center">
                  Learn More
                </Link>
              </motion.div>
            </motion.div>

            {/* Headshot */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="order-1 lg:order-2 relative"
            >
              <div className="relative max-w-sm mx-auto lg:max-w-none">
                {/* Decorative frame - hidden on small mobile */}
                <div className="hidden sm:block absolute -inset-4 border border-brand-accent/30 -z-10" />
                <div className="hidden sm:block absolute -inset-8 border border-brand-accent/10 -z-20" />
                
                <img
                  src="https://customer-assets.emergentagent.com/job_d8772972-5611-4b02-b607-8b27eec91081/artifacts/ozst57pd_Inas%20Headshot.jpg"
                  alt="Inas-Khalidah Mahdi"
                  data-testid="hero-headshot"
                  className="w-full aspect-[4/5] object-cover object-top"
                />
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-bg-primary/60 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-spacing bg-bg-secondary" data-testid="philosophy-section">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="decorative-line mb-6 sm:mb-8" />
            
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-text-primary mb-5 sm:mb-8">
              The Body Remembers What the Mind Forgets
            </h2>

            <p className="font-body text-base sm:text-lg text-text-secondary leading-relaxed mb-4 sm:mb-6">
              For too long, the Black body has been denied its full humanity — subjected to 
              violence, stripped of consent, disconnected from sensation and pleasure. This 
              is not just personal trauma. It is a{" "}
              <span className="text-text-primary">health justice failure.</span>
            </p>

            <p className="font-body text-base sm:text-lg text-text-secondary leading-relaxed mb-6 sm:mb-8">
              Embodied, consent-centered practices are not luxuries. They are public health 
              interventions. They are pathways to restoration. Through somatic healing, we 
              reclaim what was taken and remember what was always ours.
            </p>

            <p className="font-accent text-xl sm:text-2xl text-brand-accent">
              Healing is resistance. The body is the site of our liberation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Offerings Section */}
      <section className="section-spacing" data-testid="offerings-section">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 sm:mb-16"
          >
            <p className="font-body text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-brand-accent mb-3 sm:mb-4">
              Ways to Work Together
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-text-primary">
              Offerings
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {offerings.map((offering, index) => (
              <motion.div
                key={offering.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={offering.link}
                  data-testid={`offering-card-${index}`}
                  className="card block h-full group"
                >
                  <div className="text-brand-accent mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                    {offering.icon}
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl text-text-primary mb-3 sm:mb-4 group-hover:text-brand-accent transition-colors duration-300">
                    {offering.title}
                  </h3>
                  <p className="font-body text-sm sm:text-base text-text-secondary leading-relaxed mb-4 sm:mb-6">
                    {offering.description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-brand-accent text-xs sm:text-sm font-body uppercase tracking-wider">
                    Learn More 
                    <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform duration-300" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 sm:mt-12 text-center"
          >
            <Link to="/offerings" data-testid="view-all-offerings" className="btn-secondary">
              View All Offerings
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-brand-deep-green relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1744658840524-4047f33aef06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBncmVlbiUyMGdvbGQlMjBsaXF1aWQlMjBtYXJibGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc3MjAwMTc5MHww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="font-accent text-lg sm:text-xl md:text-2xl text-text-primary/80 mb-4 sm:mb-6">
              "What does it mean to come home to your body?"
            </p>
            
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-text-primary mb-5 sm:mb-8">
              Ready to Begin Your{" "}
              <span className="italic text-brand-accent">Restoration</span>?
            </h2>

            <p className="font-body text-sm sm:text-base text-text-secondary leading-relaxed mb-6 sm:mb-10">
              Whether you're seeking personal healing, community connection, or institutional 
              training, I'm here to support your journey toward embodied liberation.
            </p>

            <Link to="/book" data-testid="cta-book-button" className="btn-primary">
              Book a Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
